import {cdnImage} from '@/util/cdnImage';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {MUIContainer} from '@/components/MUI-Components';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontWeight} from '@/styles/tokens';
const checkIcon = cdnImage('white-label-exchange-development-new/icon-check-one.svg');
const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/CCI5scLEmGs?rel=0';

const bulletPoints = [
    {
        label: 'Pre-Built, Customizable Platform:',
        body: ' Tailored UI/UX, asset pairs, and admin controls aligned with your business model.'
    },
    {
        label: 'Compliance Support:',
        body: ' Guidance for KYC/AML integration, regulatory alignment, and license setup.'
    },
    {
        label: 'Faster Go-Live:',
        body: ' Deploy your branded exchange in just 6 weeks with enterprise-grade stability.'
    },
    {
        label: 'High-Performance Trading Core:',
        body: ' Low-latency engine built to handle millions of transactions per second.'
    },
    {
        label: 'Secure & Scalable Infrastructure:',
        body: ' Multi-layer encryption, wallet security, and modular APIs for seamless growth.'
    }
];

const sectionSx = {
    bgcolor: colors.background.subtle
} as const;

const headingSx = {
    fontWeight: fontWeight.extrabold,
    fontSize: {xs: 32, md: 52},
    color: colors.text.primary,
    lineHeight: 1.25,
    mb: {xs: 3, md: 4}
} as const;

const bodyTextSx = {
    fontSize: 16,
    color: colors.text.primary,
    lineHeight: '26px',
    mb: 3
} as const;

const bulletListSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    mt: 1
} as const;

const bulletItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
} as const;

const bulletTextSx = {
    fontSize: 16,
    color: colors.text.primary,
    lineHeight: '26px'
} as const;

const videoWrapperSx = {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    boxShadow: '0px 4px 24px rgba(0,0,0,0.18)'
} as const;

const WhiteLabelTurnkey = () => (
    <Box sx={sectionSx} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
        <MUIContainer>
            <Grid container spacing={{xs: 4, md: 6}} alignItems="center">
                {/* Left — text content */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h2" className="font-headers" sx={headingSx}>
                        Get Your Hands on the Turnkey Crypto Exchange Software
                    </Typography>

                    <Typography className="font-regular-text" sx={bodyTextSx}>
                        Our white label crypto exchange software is fully built, market-tested, and ready for deployment—allowing you to
                        launch a high-performance trading platform under your brand in record time. Every component, from trading engine to
                        liquidity modules, has been engineered for scalability, security, and smooth user experience.
                    </Typography>

                    <Typography className="font-regular-text remove-underline" sx={bodyTextSx}>
                        While we deliver the complete technical foundation, our experts also assist you through the compliance
                        process—helping you connect with trusted{' '}
                        <a href="https://www.debutinfotech.com/blog/blockchain-kyc" target="_blank">
                            {' '}
                            KYC
                        </a>
                        /
                        <a href="https://www.debutinfotech.com/blog/anti-money-laundering-aml-in-cryptocurrency" target="_blank">
                            AML
                        </a>{' '}
                        partners, understand licensing pathways, and ensure your exchange meets jurisdictional standards before launch.
                    </Typography>

                    <Box sx={bulletListSx}>
                        {bulletPoints.map((point, index) => (
                            <Box key={index} sx={bulletItemSx}>
                                <Box sx={{flexShrink: 0, mt: '4px'}}>
                                    <DisplayImage src={checkIcon} alt="" width={18} height={18} />
                                </Box>
                                <Typography className="font-regular-text" sx={bulletTextSx}>
                                    <Box component="strong" sx={{fontWeight: fontWeight.bold}}>
                                        {point.label}
                                    </Box>
                                    {point.body}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>

                {/* Right — YouTube embed */}
                <Grid item xs={12} md={6}>
                    <Box sx={videoWrapperSx}>
                        <iframe
                            src={YOUTUBE_EMBED_URL}
                            title="White Label Crypto Exchange"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            style={{position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none'}}
                        />
                    </Box>
                </Grid>
            </Grid>
        </MUIContainer>
    </Box>
);

export default WhiteLabelTurnkey;
